/**
 * Raffle entry badge enhancement.
 *
 * Data flow by context:
 * - Theme cards/list items: template renders price + multiplier data attributes.
 * - Page Builder product widgets: JS resolves multipliers by product path via Storefront GraphQL.
 * - PDP: template renders a badge shell and JS recalculates entries as quantity changes.
 * - Checkout: not run by theme; use Script Manager or external script if needed.
 *
 * Intentionally skipped for now:
 * - Cart
 * - Account
 */
import { loadCacheFromStorage, saveCacheToStorage } from './entries-badge/cache';
import {
    collectPriceContainersFromNode,
    getPriceContainers,
    processPriceContainers,
} from './entries-badge/containers';
import { getStorefrontApiToken, isSkippedPage } from './entries-badge/helpers';
import { startObserver } from './entries-badge/observer';
import { createPageBuilderHydrator } from './entries-badge/page-builder';
import { processPdpBadge } from './entries-badge/pdp';

const multiplierCache = loadCacheFromStorage();

const hydratePageBuilderBadges = createPageBuilderHydrator({
    multiplierCache,
    saveCache: () => saveCacheToStorage(multiplierCache),
});

function isCheckoutContext(context = {}) {
    const template = context.template || '';
    const path = window.location?.pathname || '';

    return template.includes('pages/checkout')
        || template.includes('checkout')
        || path.startsWith('/checkout')
        || Boolean(document.querySelector('.optimizedCheckout-orderSummary'));
}

const RETRY_DELAYS = [500, 1500, 3000];

function runInitialBadgePass(storefrontApiToken) {
    processPriceContainers(getPriceContainers(), storefrontApiToken);
    hydratePageBuilderBadges(storefrontApiToken);
}

function retryBadgePass(storefrontApiToken, attempt = 0) {
    if (attempt >= RETRY_DELAYS.length) {
        return;
    }

    setTimeout(() => {
        const remaining = getPriceContainers();

        if (remaining.length === 0) {
            return;
        }

        runInitialBadgePass(storefrontApiToken);
        retryBadgePass(storefrontApiToken, attempt + 1);
    }, RETRY_DELAYS[attempt]);
}

export default function entriesBadge(context = {}) {
    // Checkout: expose context for the separate checkout_entries bundle; do not run here.
    if (isCheckoutContext(context)) {
        if (typeof window !== 'undefined') {
            window.__CHECKOUT_ENTRIES_CONTEXT = context;
            try {
                window.dispatchEvent(new CustomEvent('checkoutEntriesContextReady'));
            } catch (_) {
                /* no-op */
            }
        }
        return;
    }

    if (isSkippedPage()) {
        return;
    }

    const storefrontApiToken = getStorefrontApiToken(context);

    runInitialBadgePass(storefrontApiToken);
    retryBadgePass(storefrontApiToken);

    if (document.body.classList.contains('product')) {
        processPdpBadge();
    }

    startObserver({
        storefrontApiToken,
        collectPriceContainersFromNode,
        processPriceContainers,
        hydratePageBuilderBadges,
    });
}
